import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

/**
 * Página de Verificação de Email
 * Exibe feedback ao usuário após clicar no link de verificação
 */
function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5); // Contagem regressiva de 5 segundos

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-email/${token}`);
        
        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message || 'Email verificado com sucesso!');
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Erro ao verificar email');
        }
      } catch (error) {
        setStatus('error');
        
        // Usar a mensagem específica do backend
        const errorMessage = error.response?.data?.message;
        const errorCode = error.response?.data?.code;
        
        // Se a conta já foi verificada, mostrar como sucesso parcial
        if (errorCode === 'ALREADY_VERIFIED') {
          setStatus('success');
          setMessage(errorMessage || 'Sua conta já está ativa!');
        } else {
          setMessage(
            errorMessage || 
            'Erro ao verificar email. Entre em contato com o administrador.'
          );
        }
      }
    };

    if (token) {
      verifyEmail();
    }
  }, [token]);

  // Contagem regressiva quando status for success
  useEffect(() => {
    if (status === 'success') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/login', { 
              state: { 
                message: 'Sua conta foi ativada! Faça login para continuar.',
                type: 'success'
              } 
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        {/* Loading */}
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Verificando seu email...
            </h2>
            <p className="text-gray-600">
              Por favor, aguarde enquanto confirmamos sua conta.
            </p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="text-center">
            <div className="mb-4">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto animate-bounce" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Email Verificado!
            </h2>
            <p className="text-gray-600 mb-4">
              {message}
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-medium">
                ✓ Sua conta foi ativada com sucesso
              </p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full border-4 border-green-200 flex items-center justify-center">
                    <span className="text-3xl font-bold text-green-600">
                      {countdown}
                    </span>
                  </div>
                  <svg className="absolute top-0 left-0 w-16 h-16 transform -rotate-90">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      className="text-green-600"
                      strokeDasharray={`${(countdown / 5) * 176} 176`}
                      style={{ transition: 'stroke-dasharray 1s linear' }}
                    />
                  </svg>
                </div>
                <p className="text-green-700 text-sm">
                  Redirecionando para login em <strong>{countdown}</strong> segundo{countdown !== 1 ? 's' : ''}...
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              Ir para Login Agora
              <span className="text-xs opacity-75">(ou aguarde {countdown}s)</span>
            </button>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="text-center">
            <div className="mb-4">
              <XCircle className="w-16 h-16 text-red-600 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Erro na Verificação
            </h2>
            <p className="text-gray-600 mb-4">
              {message}
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-medium text-sm mb-2">
                O que fazer?
              </p>
              <p className="text-red-700 text-sm">
                Entre em contato com o administrador do sistema para solicitar um novo link de verificação.
              </p>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Ir para Login
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Voltar para Início
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
